<template>
	<el-drawer
		direction="rtl"
        :visible.sync="drawerVisible"
		:before-close="handleClose"
		:title="`${venueRoomName} - At a glance`">
		<div class="container venue-room-details">
			<div class="checklist" v-if="venueChecklist.length">
                <div class="font-weight-bold text-dark">Checklist</div>
                <ul class="mt-2">
                    <li v-for="(item, ind) in venueChecklist" class="list-item mb-1">
						<i class="fal fa-check-circle fa-list-item"></i>
						<span class="pl-3 inline-block">{{ item }}</span>
					</li>
                </ul>
            </div>

            <div class="font-weight-bold text-dark">Gallery</div>
            <CoolLightBox
                :items="largeImages"
                :index="index"
                @close="closeLightbox">
            </CoolLightBox>
            <div class="row mt-2 mr-1 venue-room-gallery">
                <div class="col-4 py-1"
                    v-for="(image, imageIndex) in thumbnails"
                    :class="{ 'px-1': imageIndex % 3 == 1, 'px-0': imageIndex % 3 != 1 }"
                    :key="imageIndex">
                    <img
                        class="img gallery-thumb w-100"
                        @click="index = imageIndex"
                        :src="image"
                    />
                </div>
            </div>
            <div class="row score-summary-area">
                <div class="col-12 font-weight-bold text-dark header">KAREN Score breakdown</div>
                <div class="col-12">
                    <ul>
                        <li>
                            <span class="font-weight-600">Location score:</span>
                            <span class="score">{{ roomScoreSummary.location_score }}</span>
                            <span class="ml-2" v-if="locationScoreWeight">({{ locationScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Budget score:</span>
                            <span class="score">{{ roomScoreSummary.budget_score }}</span>
                            <span class="ml-2" v-if="budgetScoreWeight">({{ budgetScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Capacity score:</span>
                            <span class="score">{{ roomScoreSummary.capacity_score }}</span>
                            <span class="ml-2" v-if="capacityScoreWeight">({{ capacityScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Event style score:</span>
                            <span class="score">{{ roomScoreSummary.style_score }}</span>
                            <span class="ml-2" v-if="styleScoreWeight">({{ styleScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Outdoor space availability score:</span>
                            <span class="score">{{ roomScoreSummary.outdoor_space_score }}</span>
                            <span class="ml-2" v-if="outdoorSpaceWeight">({{ outdoorSpaceWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Accommodation score:</span>
                            <span class="score">{{ roomScoreSummary.accommodation_on_site_score }}</span>
                            <span class="ml-2" v-if="accommodationScoreWeight">({{ accommodationScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Onsite Ceremony score:</span>
                            <span class="score">{{ roomScoreSummary.ceremony_on_site_score }}</span>
                            <span class="ml-2" v-if="onsiteCeremonyScoreWeight">({{ onsiteCeremonyScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">External Catering score:</span>
                            <span class="score">{{ roomScoreSummary.external_catering_allowed_score }}</span>
                            <span class="ml-2" v-if="externalCateringScoreWeight">({{ externalCateringScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Late license score:</span>
                            <span class="score">{{ roomScoreSummary.late_license_score }}</span>
                            <span class="ml-2" v-if="lateLicenseScoreWeight">({{ lateLicenseScoreWeight }})</span>
                        </li>

                        <li>
                            <span class="font-weight-600">Mandap ceremony score:</span>
                            <span class="score">{{ roomScoreSummary.mandap_ceremony_score }}</span>
                            <span class="ml-2" v-if="mandapCeremonyScoreWeight">({{ mandapCeremonyScoreWeight }})</span>
                        </li>
                    </ul>
                </div>

                <div class="col-12 ml-4 score-total">
                    <span class="font-weight-bold text-dark">Total Score</span>
                    <span class="ml-2">{{ roomScoreSummary.total_score }}</span>
                    <span class="ml-2" v-if="totalScoringWeight">({{ totalScoringWeight }})</span>
                </div>
            </div>

            <div class="special-offers" v-if="venueSpecialOffers.length">
                <div class="font-weight-bold text-dark">Special Offers</div>
                <div v-for="offer in venueSpecialOffers" :key="offer.id" class="offer-item mt-2 mb-3">
                    <div class="offer-header">
                        <i class="fal fa-tag offer-icon"></i>
                        <span class="offer-title font-weight-600">{{ offer.title }}</span>
                    </div>
                    <div class="offer-description mt-1" v-html="offer.description"></div>
                    <div class="offer-details mt-1" v-if="offer.for_event_type || offer.start_date_utc || offer.expiry_date_utc">
                        <small class="text-muted">
                            <span v-if="offer.for_event_type" class="event-type">{{ offer.for_event_type }}</span>
                            <span v-if="offer.start_date_utc && offer.for_event_type"> • </span>
                            <span v-if="offer.start_date_utc">Valid from {{ formatDate(offer.start_date_utc) }}</span>
                            <span v-if="offer.expiry_date_utc"> to {{ formatDate(offer.expiry_date_utc) }}</span>
                        </small>
                    </div>
                </div>
            </div>
			<div class="close-area">
				<div class="row">
					<div class="col text-right">
						<el-button @click="handleClose">Close</el-button>
					</div>
				</div>
			</div>
		</div>
	</el-drawer>
</template>

<script>
import { Button, Drawer } from 'element-ui';
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';
import moment from 'moment';

export default {
	components: {
		[Drawer.name]: Drawer,
		[Button.name]: Button,
		CoolLightBox
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		venueRoomName: {
			type: String,
			default: ''
		},
		venueRoomDescription: {
			type: String,
			default: ''
		},
		venueRoomImages: {
			type: Array,
			default: () => []
		},
        venueChecklist: {
            type: Array
        },
        roomScoreSummary: {
            type: Object,
            required: true
        },
        venueSpecialOffers: {
            type: Array,
            default: () => []
        },
	},
	data() {
		return {
			index: null
		};
	},
	computed: {
        drawerVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
		thumbnails() {
			return this.venueRoomImages.map(image => image.large);
		},
		largeImages() {
			return this.venueRoomImages.map(image => image.large);
		},
        scoringSettings() {
            return this.$store.getters['recommendations/getKarenScoringSettings']
        },
        locationScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'location')?.weight;
        },
        budgetScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'pricing_per_person')?.weight;
        },
        capacityScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'number_of_guests')?.weight;
        },
        styleScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'style')?.weight;
        },
        accommodationScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'accommodation_on_site')?.weight;
        },
        onsiteCeremonyScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'ceremony_on_site')?.weight;
        },
        externalCateringScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'external_catering_permitted')?.weight;
        },
        outdoorSpaceWeight() {
            return this.scoringSettings.find(setting => setting.key == 'outdoor_space')?.weight;
        },
        lateLicenseScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'late_license')?.weight;
        },
        mandapCeremonyScoreWeight() {
            return this.scoringSettings.find(setting => setting.key == 'mandap_ceremony')?.weight;
        },
        totalScoringWeight() {
            const temp = this.scoringSettings;
            return (temp ?? []).reduce((accumulator, setting) => accumulator + parseFloat(setting?.weight), 0);
        }
	},
	methods: {
		handleClose() {
			if (this.index === null) {
				this.$emit('update:visible', false);
			}
		},
		closeLightbox() {
			this.index = null;
		},
		handleKeydown(event) {
			if (event.key === 'Escape' && this.index !== null) {
				this.closeLightbox();
				event.stopPropagation();
			}
		},
		formatDate(dateString) {
			if (!dateString) return '';
			return moment(dateString).format('Do MMMM YYYY');
		}
	},
	mounted() {
		window.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleKeydown);
	}
};
</script>


<style lang="scss">
.el-drawer__header {
	span[role="heading"] {
		color: #000000;
		font-weight: 600;
        outline: 0;
	}
}

.venue-room-details {
    padding: 0px 20px;
    height: calc(100vh - 60px);
    overflow-y: auto;
    overflow-x: hidden;

    .checklist {

        ul {
            padding-inline-start: 0;
		}

		li{
			color: rgba(0, 0, 0, 0.6);
			list-style: none;
		}

		.fa-list-item {
			display: inline-block;
			width: 1.5em;
			text-align: center;
		}
		.list-item {
			display: flex;
			align-items: baseline;
		}
		.list-item span {
			flex: 1;
		}

		i.fa-list-item {
			color: lightseagreen;
		}

    }

    .special-offers {
        margin: 30px 0px;

        .offer-item {
            padding: 12px;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            background-color: #f8f9fa;

            .offer-header {
                display: flex;
                align-items: center;

                .offer-icon {
                    color: #28a745;
                    margin-right: 8px;
                }

                .offer-title {
                    color: #495057;
                }
            }

            .offer-description {
                color: rgba(0, 0, 0, 0.7);
                font-size: 14px;
                line-height: 1.4;
            }

            .offer-details {
                color: #6c757d;
            }
        }
    }

	.venue-room-gallery {
        padding-left: 20px;
		.gallery-thumb {
			cursor: pointer;
			height: 110px;
			object-fit: cover;
			transition: opacity 0.25s ease-in-out;

			&:hover {
				opacity: 0.75;
			}
		}
	}
    .score-summary-area {
        margin-top: 50px;

        > div.header {
            margin-bottom: 10px;
        }
        ul li span.score {
            margin-left: 10px;
        }
    }
}

.close-area {
	position: fixed;
	bottom: 15px;
	right: 15px;
}
</style>
